/**
 * Bezárás-felismerés és a közrezárt terület megtalálása.
 *
 * Ez a GRUNDO legfontosabb algoritmusa. A hexagon-rács miatt nem poligon-
 * algebra, hanem flood fill: determinisztikus, egészszám-alapú, és nem tud
 * érvénytelen geometriát előállítani. Emiatt a kliens és a szerver bitre
 * ugyanazt az eredményt adja.
 *
 * Szabály (docs/03-jatekszabalyok.md): a bezárás bármely ÖNMETSZÉS. Nem kell
 * visszaérni a rajthoz, és egy aktivitás alatt több bezárás is lehet.
 */

import {
  cellToLatLng,
  polygonToCells,
  getResolution,
} from 'h3-js';
import { GAMEPLAY } from '@/config/gameplay';
import type {
  CellId,
  DetectedLoop,
  LoopDiagnostics,
  RejectedLoopDiagnostic,
} from '@/types';
import { childrenOf, parentOf, ringOf } from './neighbours';

export class LoopTooLargeError extends Error {
  constructor(public readonly candidateCells: number) {
    super(`A hurok túl nagy (${candidateCells} cella) — valószínűleg GPS-hiba.`);
    this.name = 'LoopTooLargeError';
  }
}

/**
 * Végigmegy a cellaláncon, és minden önmetszésnél megpróbálja bezárni a hurkot.
 *
 * Az ismételt kör (körbe-körbe futás) újra detektálódik — ez szándékos:
 * pontosan ez adja a védelemnövelést és a 2–5× pontszorzót.
 */
/**
 * A falból CSAK a körökön fekvő mezők maradnak.
 *
 * A fal a két találkozás között bejárt cellák halmaza — kitérőkkel és
 * összekötő szakaszokkal együtt. Kétféle mező kerül bele feleslegesen:
 *
 *   ZSÁKUTCA — kimész néhány mezőt és ugyanazon jössz vissza. Ezeknek csak
 *   egy szomszédjuk van a falban, mint egy kinyúló ujjnak.
 *
 *   ÖSSZEKÖTŐ FOLYOSÓ — két bezárt terület között átmész egy vonalon, majd
 *   ugyanazon jössz vissza. Ezeknek KÉT szomszédjuk van, tehát a zsákutca-
 *   szabály nem fogja meg őket — mégsem részei egyetlen körnek sem.
 *
 * A közös bennük, hogy egyik sem fekszik körön. Gráfelméletben az ilyen élek
 * a HIDAK: olyan él, amit elvágva a gráf szétesik. Egy körön fekvő él sosem
 * híd, hiszen a kör másik fele megkerüli.
 *
 * Az eljárás tehát: megkeressük a hidakat, elvágjuk őket, és eldobjuk azokat
 * a cellákat, amiknek ezután nem marad éle. Ez egy menetben elintézi mindkét
 * esetet — a kinyúló ujjakat és az összekötő folyosókat is.
 *
 * A VALÓDI kört nem érinti: ott minden él körön fekszik, tehát egyik sem híd.
 *
 * A metszés a flood fill ELŐTT fut. Ha utána tennénk, egy befelé mutató
 * kitérő cellái se falként, se belsőként nem szerepelnének — lyuk maradna a
 * területben. Így viszont a levágott cellák a belsőhöz kerülnek, ami helyes.
 */
export function pruneDeadEnds(wall: ReadonlySet<CellId>): Set<CellId> {
  const neighbours = new Map<CellId, CellId[]>();
  for (const cell of wall) {
    const list: CellId[] = [];
    for (const near of ringOf(cell)) {
      if (near !== cell && wall.has(near)) list.push(near);
    }
    neighbours.set(cell, list);
  }

  const bridges = findBridges(neighbours);
  const kept = new Set<CellId>();

  for (const [cell, list] of neighbours) {
    // Marad, ha van legalább egy éle, ami NEM híd — azaz körön fekszik.
    if (list.some((near) => !bridges.has(edgeKey(cell, near)))) kept.add(cell);
  }

  return kept;
}

function edgeKey(a: CellId, b: CellId): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Hídkeresés (Tarjan), ITERATÍVAN.
 *
 * Rekurzívan rövidebb lenne, de egy hosszú aktivitás fala több ezer cellából
 * áll, és egy elnyúlt útvonalon a mélység is ennyi lehet — az pedig
 * veremtúlcsordulás. A saját veremmel kezelt változat ettől mentes.
 */
function findBridges(neighbours: ReadonlyMap<CellId, readonly CellId[]>): Set<string> {
  const bridges = new Set<string>();
  const discovered = new Map<CellId, number>();
  const lowest = new Map<CellId, number>();
  let time = 0;

  for (const root of neighbours.keys()) {
    if (discovered.has(root)) continue;

    // [cella, szülő, hányadik szomszédnál tartunk]
    const stack: [CellId, CellId | null, number][] = [[root, null, 0]];
    discovered.set(root, time);
    lowest.set(root, time);
    time += 1;

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;
      const [cell, parent, index] = frame;
      const list = neighbours.get(cell) ?? [];

      if (index < list.length) {
        frame[2] += 1;
        const near = list[index]!;
        if (near === parent) continue;

        if (discovered.has(near)) {
          // Visszaél: a mélyebb pont ennél nem lehet magasabb.
          lowest.set(cell, Math.min(lowest.get(cell)!, discovered.get(near)!));
        } else {
          discovered.set(near, time);
          lowest.set(near, time);
          time += 1;
          stack.push([near, cell, 0]);
        }
        continue;
      }

      stack.pop();
      if (parent !== null) {
        lowest.set(parent, Math.min(lowest.get(parent)!, lowest.get(cell)!));
        // Ha a részfából nem vezet vissza él a szülő fölé, az él HÍD.
        if (lowest.get(cell)! > discovered.get(parent)!) {
          bridges.add(edgeKey(parent, cell));
        }
      }
    }
  }

  return bridges;
}

/**
 * ⚠️ LEVÁLTOTT DETEKTOR — ÉLES KÓDBÓL NE HÍVD.
 *
 * A `src/game` belépőpont 5cf6362 (2026-08-24) óta a `loopDetection.ts`
 * átfedéskezelő detektorát exportálja `detectLoopsDetailed` néven, ami
 * ELFEDI ezt az azonos nevű, régebbi változatot. Aki közvetlenül ebből a
 * modulból importál, csendben a régi szabályok szerint számol tovább.
 *
 * Mért eltérés: ugyanannál a H3 kontaktfoltnál ez a változat a LEGKORÁBBI
 * kaput választja, az élő detektor a legfrissebbet — egy 220 m-es
 * fixture-körnél 57 vs. 56 falcella. A `routes/missions` emiatt hónapokig
 * más cellahalmazt ígért, mint amit a `processActivity` jóváírt
 * (`missionEvaluate.shapeCandidateCells`, 2026-08-25).
 *
 * Ami még használja: `geometry.test.ts` és `claim.test.ts` — azok
 * kifejezetten ennek a változatnak a szabályait rögzítik.
 */
export function detectLoopsDetailed(path: readonly CellId[]): {
  loops: DetectedLoop[];
  diagnostics: LoopDiagnostics;
} {
  const loops: DetectedLoop[] = [];
  const successful: LoopDiagnostics['successful'] = [];
  const rejected: RejectedLoopDiagnostic[] = [];
  let shortRevisits = 0;
  const lastSeenAt = new Map<CellId, number>();

  for (let i = 0; i < path.length; i++) {
    const cell = path[i]!;
    const sameCellPrevious = lastSeenAt.get(cell);

    if (sameCellPrevious !== undefined && i - sameCellPrevious < GAMEPLAY.MIN_LOOP_STEPS) {
      shortRevisits += 1;
    }

    /**
     * A hurok a CELLAFALON záródik, nem a GPS-vonal matematikai
     * keresztezésén. Ezért nemcsak a most újra meglátogatott cella lehet
     * kapu, hanem bármely korábban bejárt ÉLSZOMSZÉDJA is. A felhasználó
     * képein a két fal már összeért, a középpontvonal viszont néhány méterrel
     * elkerülte önmagát — a régi, azonos-cellás feltétel ezt tévesen nyitott
     * huroknak látta.
     *
     * A közvetlen előzmények természetesen mindig szomszédok, ezért csak a
     * strukturális minimumon túli jelöltek számítanak. Több kapunál a
     * legkorábbit próbáljuk először: ez adja a teljes, ténylegesen körbejárt
     * falat; a flood fill és a minimum továbbra is kiszűri az üres érintést.
     */
    const exactCandidate = sameCellPrevious !== undefined &&
      i - sameCellPrevious >= GAMEPLAY.MIN_LOOP_STEPS
      ? sameCellPrevious
      : undefined;
    const neighbourCandidates = [...new Set(
      ringOf(cell)
        .filter((near) => near !== cell)
        .map((near) => lastSeenAt.get(near))
        .filter((index): index is number =>
          index !== undefined && i - index >= GAMEPLAY.MIN_LOOP_STEPS),
    )].sort((a, b) => a - b);
    // Ha a GPS ténylegesen ugyanabba a cellába ért vissza, a korábbi,
    // jól bevált kapu az elsődleges. A szomszédos zárás csak fallback, így
    // nem vágunk le egy cellát a hagyományos körök falából.
    const candidateIndices = exactCandidate === undefined
      ? neighbourCandidates
      : [exactCandidate, ...neighbourCandidates.filter((index) => index !== exactCandidate)];

    for (const previous of candidateIndices) {
      // A zsákutcákat a bezárás ELŐTT vágjuk le: ami csak egy szomszéddal
      // érintkezik, az nem része a körnek. Lásd `pruneDeadEnds`.
      const rawWall = new Set(path.slice(previous, i + 1));
      const wall = pruneDeadEnds(rawWall);
      const prunedCells = Math.max(0, rawWall.size - wall.size);
      let interior: Set<CellId>;
      try {
        interior = floodFillInterior(wall);
      } catch (err) {
        if (err instanceof LoopTooLargeError) {
          rejected.push({
            reason: 'too_large',
            fromIndex: previous,
            toIndex: i,
            wallCells: wall.size,
            interiorCells: 0,
            prunedCells,
            candidateCells: err.candidateCells,
          });
          continue;
        }
        throw err;
      }

      if (interior.size >= GAMEPLAY.MIN_INTERIOR_CELLS) {
        loops.push({ wall, interior, fromIndex: previous, toIndex: i });
        successful.push({
          fromIndex: previous,
          toIndex: i,
          wallCells: wall.size,
          interiorCells: interior.size,
          prunedCells,
        });
        /**
         * A bezárás után a TELJES előzmény elfogy — nem csak a felhasznált
         * szakasz.
         *
         * ⚠️ EZ EGY ÉLES HIBA JAVÍTÁSA (2026-08-19). Korábban csak a
         * `[previous, i)` szakasz mezőit felejtettük el. Ez elég annak, hogy
         * két EGYMÁS UTÁNI kör külön bezárásként számítson, de nem véd az
         * ellen, ami a valódi használatban szinte mindig előfordul:
         *
         *   A felhasználó a kaputól indítja a rögzítést, kimegy a körhöz,
         *   megcsinálja, és UGYANAZON az úton jön vissza.
         *
         * A rávezető szakasz mezőit így kétszer érinti: egyszer az elején,
         * egyszer a legvégén. A két érintés KÖZÖTT viszont ott van az egész
         * bezárt terület. Minden ilyen mező tehát újabb „bezárást" szült
         * ugyanarra a területre — a `pruneDeadEnds` a rávezetőt hídként
         * levágta, maradt a kör fala, valódi belsővel.
         *
         * Mért következmény egy valódi, 11 km-es körnél: 26 detektált hurok,
         * minden mező 5-ös védelmen (a maximumon), és 631 igénypont a helyes
         * 126 helyett. Már 40 méter rávezető szakasz maxra vitte a védelmet.
         *
         * A teljes törléssel a szabály egyszerű és kimondható: a következő
         * bezárás csak a MOSTANI bezárás UTÁN bejárt útból épülhet. Az
         * ismételt kör jutalma sértetlen marad — négy kör továbbra is négy
         * hurok és 4-es védelem —, mert a második kör a saját mezőin záródik.
         *
         * Regressziós tesztek: `geometry.test.ts` → „rávezető szakasz".
         */
        lastSeenAt.clear();
        break;
      } else {
        rejected.push({
          reason: 'interior_too_small',
          fromIndex: previous,
          toIndex: i,
          wallCells: wall.size,
          interiorCells: interior.size,
          prunedCells,
        });
      }
    }

    lastSeenAt.set(cell, i);
  }

  return { loops, diagnostics: { successful, rejected, shortRevisits } };
}

export function detectLoops(path: readonly CellId[]): DetectedLoop[] {
  return detectLoopsDetailed(path).loops;
}

/**
 * A fal által közrezárt cellák megkeresése.
 *
 * 1. jelöltrégió: a fal befoglaló doboza + margó, cellákra bontva
 * 2. a régió peremcelláiból induló szélességi bejárás, a fal akadály
 * 3. amit kívülről nem értünk el → belső
 */
export function floodFillInterior(wall: ReadonlySet<CellId>): Set<CellId> {
  if (wall.size === 0) return new Set();
  return prefersAdaptiveFill(wall)
    ? floodFillInteriorAdaptive(wall)
    : floodFillInteriorExact(wall);
}

/**
 * A TERÜLET-arányos, pontos menet.
 *
 * A `floodFillInterior` választja ki, de kívülről is elérhető, mert a
 * gyorsítás egésze azon a feltevésen áll, hogy a két út UGYANAZT adja — és
 * ezt tesztelni kell tudni (`loops.fill.test.ts`), nem elhinni.
 */
export function floodFillInteriorExact(wall: ReadonlySet<CellId>): Set<CellId> {
  const first = wall.values().next().value as CellId | undefined;
  if (first === undefined) return new Set();
  const res = getResolution(first);

  // A méretkorlát a polyfill ELŐTT dől el — lásd candidateRegion().
  const candidates = candidateRegion(wall, res);

  // A régió pereme: minden olyan cella, amelynek van szomszédja a régión kívül.
  const queue: CellId[] = [];
  const outside = new Set<CellId>();
  for (const cell of candidates) {
    if (wall.has(cell)) continue;
    for (const n of ringOf(cell)) {
      if (!candidates.has(n)) {
        outside.add(cell);
        queue.push(cell);
        break;
      }
    }
  }

  // Szélességi bejárás kívülről befelé, a falon nem lépünk át.
  while (queue.length > 0) {
    const cell = queue.pop()!;
    for (const n of ringOf(cell)) {
      if (!candidates.has(n) || wall.has(n) || outside.has(n)) continue;
      outside.add(n);
      queue.push(n);
    }
  }

  const interior = new Set<CellId>();
  for (const cell of candidates) {
    if (!wall.has(cell) && !outside.has(cell)) interior.add(cell);
  }
  return interior;
}

/**
 * MELYIK KITÖLTÉS FUSSON?
 *
 * A pontos menet a TERÜLETTEL nő (a fal befoglaló dobozát cellákra bontja),
 * az adaptív a KERÜLETTEL (a falat két felbontással feljebb viszi, és csak a
 * határsávot bontja vissza). A helyes döntés tehát nem egy abszolút
 * cellaszám, hanem a kettő ARÁNYA: mennyi terület jut egy falcellára.
 *
 * ⚠️ EZ KORÁBBAN ABSZOLÚT SZÁM VOLT (40 000 cella ≈ 12 km²), és ez volt a
 * hosszú aktivitások mentésének fő költsége. MÉRVE (2026-09-02, 55 km-es
 * városi rács, 3 515 cellás nyomvonal): a hurokdetektor jelöltjeinek fala
 * átlagosan 1 651 cella, a befoglaló dobozuk viszont több tízezer — vagyis
 * pont a rossz oldalán álltak a 40 000-es küszöbnek, és mind a drága, terület-
 * arányos úton mentek.
 *
 * A `buildActivityGeometry` MÉRT ideje ugyanarra a nyomvonalra:
 *
 *   92,5 s  kiinduló állapot
 *   40,0 s  a szomszéd-gyorsítótár után (`neighbours.ts` `ringOf`)
 *   27,4 s  arány = 8
 *   18,4 s  arány = 2        ← ez van beállítva
 *   17,8 s  mindig adaptív (arány = 0)
 *
 * A teljes, 126 km-es menet `planActivity`-je ugyanezekkel a lépésekkel, plusz
 * a felbontásváltás memoizálásával (`parentOf`/`childrenOf`): 453,6 s → 51,0 s.
 *
 * A 2-es a mért optimum gyakorlati széle: az „mindig adaptív"-hoz képest 3%
 * a különbség, cserébe a valóban vékony, belső nélküli jelöltek (folyosó,
 * zsákutca — ahol a doboz alig nagyobb a falnál) maradnak az egyszerűbb,
 * egymenetes úton. A hurkok száma és a belsejük mind az öt beállításnál
 * azonos volt; ezt a `loops.fill.test.ts` rögzíti is.
 */
const ADAPTIVE_AREA_PER_WALL_CELL = 2;

function prefersAdaptiveFill(wall: ReadonlySet<CellId>): boolean {
  return estimateRegionCells(wall) > ADAPTIVE_AREA_PER_WALL_CELL * wall.size;
}

/**
 * Hány felbontással durvább a durva menet?
 *
 * Kettő lépés = 49-szer kevesebb cella. Egy res 10 hatszög ~150 m átmérőjű,
 * ami a valódi hurkok falához képest elég finom ahhoz, hogy a határsáv
 * keskeny maradjon.
 */
const COARSE_STEPS = 2;

/**
 * Ennél több belső cellát nem állítunk elő.
 *
 * ⚠️ EZ A VALÓDI KORLÁT, és nem a kitöltésé — hanem az EREDMÉNYÉ. Az adaptív
 * menet a befoglaló doboz plafonját feloldotta, de a megtalált cellákat akkor
 * is tárolni kell: mérve ~420 bájt cellánként (a halmaz és a hurokkönyvelés
 * együtt). 1,2 millió cella így ~500 MB — 2,2 millió cella így ~920 MB.
 *
 * Ehhez a Cloud Run 2 GiB-re van állítva (`cloudbuild.yaml`) — a kettő
 * EGYÜTT érvényes, külön-külön egyik sem elég. Ha a memória visszamegy
 * 512 MiB-re, ez a szám is le kell hogy jöjjön, különben a kérés nem
 * hibaüzenetet ad, hanem elfogy alatta a memória.
 *
 * Területben ez ~675 km², vagyis egy nagyjából 104 km kerületű kör — a
 * Balaton-kör (~600 km²) tehát BELEFÉR. Felbontásváltás nem kell hozzá.
 */
const MAX_CLAIM_CELLS = 2_200_000;

/**
 * Kitöltés két menetben — a munka a KERÜLETTEL nő, nem a területtel.
 *
 * A sima `floodFillInterior` a teljes befoglaló dobozt cellákra bontja, és
 * azon fut végig. Egy 143 km²-es hurok így félmillió cellát jelent — ez a
 * `MAX_LOOP_BBOX_CELLS` plafonja, és emiatt esett ki eddig minden nagyobb kör.
 *
 * AZ ELJÁRÁS:
 *   1. DURVA MENET — a falat felvisszük két felbontással feljebb, és ott
 *      futtatjuk a kitöltést. 49-szer kevesebb cella.
 *   2. FINOM MENET — csak a HATÁRSÁVBAN (azokban a durva cellákban, amikben
 *      fal van) bontunk vissza az eredeti felbontásra, és ott döntjük el
 *      cellánként, mi kívül és mi belül van.
 *   3. VISSZATERJESZTÉS — ha a finom menet olyan cellát talál kívülnek, ami
 *      belsőnek jelölt durva cellával szomszédos, azt a durva cellát is
 *      kinyitjuk, és a durva kitöltést onnan folytatjuk.
 *
 * ⚠️ A HARMADIK LÉPÉS NEM ELHAGYHATÓ. Nélküle egy szűk folyosó — ami kívülről
 * bevezet egy öbölbe, de végig olyan durva cellákban fut, amikben fal is van —
 * észrevétlen maradna, és az öböl tévesen a felhasználóé lenne. Ritka alakzat,
 * de csendben rossz területet adna.
 */
/**
 * AZ ADAPTÍV KITÖLTÉS DURVA ELŐKÉSZÜLETE — a DURVA fal tiszta függvénye.
 *
 * ── MIÉRT ÉRDEMES GYORSÍTÓTÁRAZNI ────────────────────────────────────────
 *
 * A hurokdetektor minden új cellánál új jelöltet vizsgál, és a jelöltek fala
 * cellánként alig tér el — két felbontással feljebb (res10) viszont a
 * KÜLÖNBSÉG EL IS TŰNIK. Mérve a 2026-09-04-i terepi nyomvonalon
 * (`docs/ai/meres-2026-09-04-terepi-fosszal.md`, `tmp/measure-adaptive-internals.ts`):
 * **499 jelölt, de mindössze 29 különböző durva fal.** A durva előkészület
 * — befoglaló polyfill, durva kitöltés, sáv, sávperem — a kitöltés idejének
 * 62%-a volt, és ezt 499-szer fizettük 29 helyett.
 *
 * ── MIÉRT NEM KÖZELÍTÉS ──────────────────────────────────────────────────
 *
 * Mind a négy eredmény a durva fal DETERMINISZTIKUS függvénye, felbontással
 * együtt. Nincs elavulás, nincs tűrés: ugyanarra a bemenetre bitre ugyanaz
 * jön vissza, mint számolással. A `loops.fill.test.ts` a pontos és az adaptív
 * menet egyezését amúgy is méri, tehát egy elrontott gyorsítótár ott bukik.
 *
 * ⚠️ A `outside` MÁSOLATKÉNT adandó tovább — a hívó a visszaterjesztésnél
 * mutálja. A `region` és a `band` csak olvasásra megy.
 */
interface CoarseContext {
  region: ReadonlySet<CellId>;
  /**
   * A durva menet UTÁNI kültér, a finom fal ismerete nélkül.
   *
   * ⚠️ `ReadonlySet` SZÁNDÉKOSAN. A hívó a visszaterjesztésnél (3. lépés) a
   * FINOM fal alapján tovább nyitja a kültért — ha ezt a halmazt mutálná, a
   * bejegyzés beszennyeződne, és a következő, ugyanilyen durva falú jelölt
   * hibásan nagy kültérrel indulna: kisebb belsőt, azaz KEVESEBB TERÜLETET
   * adna a felhasználónak, csendben. A típus kényszeríti ki a másolatot —
   * ezt ugyanis egy teszt nem fogná meg megbízhatóan: a visszaterjesztés csak
   * ritka alakzatra (öbölbe vezető szűk folyosó) fut le egyáltalán.
   */
  outside: ReadonlySet<CellId>;
  band: ReadonlySet<CellId>;
  /** A sáv azon cellái, amik a kültérrel érintkeznek — a finom menet magjai. */
  bandBorder: readonly CellId[];
}

/**
 * Ennyi durva falat őrzünk meg.
 *
 * Egy bejegyzés a sávval együtt néhány ezer azonosító. A korlát elérésekor a
 * teljes tartalmat eldobjuk, nem egyenként avulunk — ugyanaz a megfontolás,
 * mint a `neighbours.ts`-ben: egy LRU könyvelése többe kerülne, mint amennyit
 * megspórol, a nyereség pedig úgyis EGY aktivitás feldolgozásán belül jön.
 */
const MAX_CACHED_CONTEXTS = 64;

/**
 * Ennél nagyobb előkészületet nem tárolunk.
 *
 * ⚠️ Egy Balaton-méretű hurok sávja több millió cella is lehet; abból 64-et
 * megtartani félgigás nagyságrend a Cloud Runon (`MAX_CLAIM_CELLS` ott ~920 MB
 * a csúcs). Az ilyen hurokból amúgy sincs sok egy aktivitásban, tehát a
 * gyorsítótár nem is nyerne rajta — a kihagyás itt olcsóbb, mint a kockázat.
 */
const MAX_CACHED_CONTEXT_CELLS = 200_000;

const coarseContexts = new Map<string, CoarseContext>();

/** Kanonikus kulcs: a halmaz bejárási sorrendje hívásonként más lehet. */
function coarseKeyOf(coarseWall: ReadonlySet<CellId>, coarseRes: number, res: number): string {
  return `${coarseRes}:${res}:${[...coarseWall].sort().join(',')}`;
}

function coarseContextOf(
  coarseWall: ReadonlySet<CellId>,
  coarseRes: number,
  res: number,
): CoarseContext {
  const key = coarseKeyOf(coarseWall, coarseRes, res);
  const cached = coarseContexts.get(key);
  if (cached !== undefined) return cached;

  const region = candidateRegion(coarseWall, coarseRes);
  const outside = new Set<CellId>();
  const queue: CellId[] = [];

  for (const cell of region) {
    if (coarseWall.has(cell)) continue;
    for (const near of ringOf(cell)) {
      if (!region.has(near)) {
        outside.add(cell);
        queue.push(cell);
        break;
      }
    }
  }
  while (queue.length > 0) {
    const cell = queue.pop()!;
    for (const near of ringOf(cell)) {
      if (!region.has(near) || coarseWall.has(near) || outside.has(near)) continue;
      outside.add(near);
      queue.push(near);
    }
  }

  const band = new Set<CellId>();
  for (const coarse of coarseWall) {
    for (const child of childrenOf(coarse, res)) band.add(child);
  }

  const bandBorder: CellId[] = [];
  for (const cell of band) {
    for (const near of ringOf(cell)) {
      if (band.has(near)) continue;
      const parent = parentOf(near, coarseRes);
      if (outside.has(parent) || !region.has(parent)) {
        bandBorder.push(cell);
        break;
      }
    }
  }

  const context: CoarseContext = { region, outside, band, bandBorder };
  if (region.size + band.size <= MAX_CACHED_CONTEXT_CELLS) {
    if (coarseContexts.size >= MAX_CACHED_CONTEXTS) coarseContexts.clear();
    coarseContexts.set(key, context);
  }
  return context;
}

export function floodFillInteriorAdaptive(wall: ReadonlySet<CellId>): Set<CellId> {
  const first = wall.values().next().value as CellId | undefined;
  if (first === undefined) return new Set();
  const res = getResolution(first);
  const coarseRes = Math.max(0, res - COARSE_STEPS);

  /* ── 1. Durva menet ─────────────────────────────────────────────── */

  const coarseWall = new Set<CellId>();
  for (const cell of wall) coarseWall.add(parentOf(cell, coarseRes));

  const context = coarseContextOf(coarseWall, coarseRes, res);
  const coarseRegion = context.region;
  /**
   * MÁSOLAT, nem a gyorsítótárazott halmaz: a 3. lépés (visszaterjesztés) ezt
   * a FINOM fal alapján tovább nyitja, tehát a bejegyzés beszennyeződne, és a
   * következő jelölt hibásan nagy kültérrel indulna.
   */
  const coarseOutside = new Set<CellId>(context.outside);

  /* ── 2. Finom menet a határsávban ───────────────────────────────── */

  /** A határsáv finom cellái: a falat tartalmazó durva cellák gyerekei. */
  const band = context.band;

  const fineOutside = new Set<CellId>();
  const fineQueue: CellId[] = [];
  /**
   * Kívülről indulunk: a sáv azon cellái, amiknek van szomszédjuk egy KÍVÜLNEK
   * ismert durva cellában (vagy a régión kívül). Ez a halmaz KIZÁRÓLAG a durva
   * faltól függ, ezért a `coarseContextOf` már kiszámolta — itt csak a falban
   * fekvő cellákat kell kiszűrni belőle, ami jelöltenként változik.
   */
  for (const cell of context.bandBorder) {
    if (wall.has(cell)) continue;
    fineOutside.add(cell);
    fineQueue.push(cell);
  }
  spreadFine(fineQueue);

  /* ── 3. Visszaterjesztés a durva szintre ────────────────────────── */

  for (;;) {
    const opened: CellId[] = [];
    for (const cell of fineOutside) {
      for (const near of ringOf(cell)) {
        if (band.has(near) || wall.has(near)) continue;
        const parent = parentOf(near, coarseRes);
        if (coarseRegion.has(parent) && !coarseOutside.has(parent) && !coarseWall.has(parent)) {
          coarseOutside.add(parent);
          opened.push(parent);
        }
      }
    }
    if (opened.length === 0) break;
    spreadCoarse(opened);

    // Az újonnan kinyílt durva cellák új belépőket adhatnak a sávba.
    const again: CellId[] = [];
    for (const cell of band) {
      if (wall.has(cell) || fineOutside.has(cell)) continue;
      for (const near of ringOf(cell)) {
        if (band.has(near)) continue;
        if (coarseOutside.has(parentOf(near, coarseRes))) {
          fineOutside.add(cell);
          again.push(cell);
          break;
        }
      }
    }
    if (again.length === 0) break;
    spreadFine(again);
  }

  /* ── Az eredmény összeállítása ──────────────────────────────────── */

  /**
   * A méretellenőrzés az ÖSSZEÁLLÍTÁS ELŐTT.
   *
   * Ha utána tennénk, már lefoglaltuk volna azt a memóriát, ami miatt a
   * korlát egyáltalán létezik. A durva cellák gyerekszáma pontosan ismert,
   * tehát a becslés itt nem közelítés, hanem számolás.
   */
  const childrenPerCoarse = 7 ** COARSE_STEPS;
  let expected = band.size;
  for (const coarse of coarseRegion) {
    if (!coarseOutside.has(coarse) && !coarseWall.has(coarse)) expected += childrenPerCoarse;
  }
  if (expected > MAX_CLAIM_CELLS) throw new LoopTooLargeError(expected);

  const interior = new Set<CellId>();
  for (const coarse of coarseRegion) {
    if (coarseOutside.has(coarse)) continue;
    if (coarseWall.has(coarse)) continue;
    // Teljesen belső durva cella: MINDEN gyereke belső, egyenként vizsgálat
    // nélkül. Innen jön a nyereség.
    for (const child of childrenOf(coarse, res)) interior.add(child);
  }
  for (const cell of band) {
    if (!wall.has(cell) && !fineOutside.has(cell)) interior.add(cell);
  }
  return interior;

  function spreadCoarse(start: CellId[]): void {
    const stack = [...start];
    while (stack.length > 0) {
      const cell = stack.pop()!;
      for (const near of ringOf(cell)) {
        if (!coarseRegion.has(near) || coarseWall.has(near) || coarseOutside.has(near)) continue;
        coarseOutside.add(near);
        stack.push(near);
      }
    }
  }

  function spreadFine(start: CellId[]): void {
    const stack = [...start];
    while (stack.length > 0) {
      const cell = stack.pop()!;
      for (const near of ringOf(cell)) {
        if (!band.has(near) || wall.has(near) || fineOutside.has(near)) continue;
        fineOutside.add(near);
        stack.push(near);
      }
    }
  }
}

/**
 * Hány cellát adna a fal befoglaló doboza az ADOTT felbontáson?
 *
 * Olcsó közelítés — a polyfill lefuttatása nélkül. Ez dönti el, melyik
 * kitöltés fut, és a méretkorlát is ebből ítél.
 */
function estimateRegionCells(wall: ReadonlySet<CellId>): number {
  const first = wall.values().next().value as CellId | undefined;
  const res = first === undefined ? GAMEPLAY.H3_RESOLUTION : getResolution(first);
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  for (const cell of wall) {
    const [lat, lng] = cellToLatLng(cell);
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }
  const pad = 0.0006;
  return estimateCellCount(minLat - pad, maxLat + pad, minLng - pad, maxLng + pad, res);
}

/** A fal befoglaló doboza + margó, cellákra bontva. */
function candidateRegion(wall: ReadonlySet<CellId>, res: number): Set<CellId> {
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  for (const cell of wall) {
    const [lat, lng] = cellToLatLng(cell);
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  }

  // ~60 m margó, hogy a peremcellák biztosan a falon kívül essenek
  const pad = 0.0006;
  const south = minLat - pad;
  const north = maxLat + pad;
  const west = minLng - pad;
  const east = maxLng + pad;

  // A méretkorlátot a polyfill ELŐTT kell ellenőrizni.
  //
  // Ha utána tennénk, egy vonat- vagy repülőút befoglaló doboza milliárdos
  // nagyságrendű cellahalmazt generálna, mielőtt egyáltalán megnéznénk, hogy
  // túl nagy-e — ami memóriát fal és percekre megállítja a feldolgozást.
  // A doboz területéből becsülni olcsó és elég pontos.
  const estimated = estimateCellCount(south, north, west, east, res);
  if (estimated > GAMEPLAY.MAX_LOOP_BBOX_CELLS) {
    throw new LoopTooLargeError(estimated);
  }

  const boundary: [number, number][] = [
    [south, west],
    [south, east],
    [north, east],
    [north, west],
  ];

  return new Set(polygonToCells([boundary], res));
}

/** Hány cella férne a befoglaló dobozba? Közelítés, a polyfill elkerülésére. */
function estimateCellCount(
  south: number,
  north: number,
  west: number,
  east: number,
  res: number = GAMEPLAY.H3_RESOLUTION,
): number {
  const M_PER_DEG = 111_320;
  const midLat = ((south + north) / 2) * (Math.PI / 180);
  const heightM = (north - south) * M_PER_DEG;
  const widthM = (east - west) * M_PER_DEG * Math.cos(midLat);
  return Math.abs(heightM * widthM) / cellAreaAt(res);
}

/**
 * Egy cella területe az adott felbontáson.
 *
 * ⚠️ EZ NEM ELHANYAGOLHATÓ RÉSZLET. A becslő korábban FIXEN a res 12 méretével
 * számolt, és emiatt az adaptív kitöltés durva régióját is annak alapján
 * ítélte túl nagynak — a 48 km fölötti köröket a motor csendben eldobta, pedig
 * a durva menetben bőven elfértek volna.
 *
 * A H3-nál minden felbontáslépés hetedére osztja a cellát.
 */
function cellAreaAt(res: number): number {
  return GAMEPLAY.CELL_AREA_M2 * 7 ** (GAMEPLAY.H3_RESOLUTION - res);
}

/** Egy bezárás összes megszerzett cellája: a fal és a belső együtt. */
export function loopCells(loop: DetectedLoop): Set<CellId> {
  const all = new Set<CellId>(loop.interior);
  for (const cell of loop.wall) all.add(cell);
  return all;
}
